import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageTitle } from '../../components/PageTitle';

const RestaurantApps = () => {
  return (
    <div>
      <PageTitle 
        title="Restaurant Apps - Custom Mobile Applications for Restaurants | CodeSplash"
        description="Custom restaurant mobile apps for online ordering, table reservations, and loyalty programs. Streamline operations and increase revenue with digital solutions."
        canonicalUrl="https://codesplashdesigns.com/services/restaurant-apps"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Restaurant Apps
            </h1>
            <p className="text-xl mb-8">
              Custom mobile applications and digital solutions for modern restaurants.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="p-6 bg-white rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-semibold mb-4">Online Ordering System</h3>
              <p className="text-gray-600">
                Seamless mobile ordering experience with real-time order tracking and notifications.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-6 bg-white rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-semibold mb-4">Table Reservation</h3>
              <p className="text-gray-600">
                Digital table booking system with automated confirmation and reminder notifications.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="p-6 bg-white rounded-lg shadow-lg"
            >
              <h3 className="text-xl font-semibold mb-4">Loyalty Program</h3>
              <p className="text-gray-600">
                Digital loyalty programs to reward customers and drive repeat business.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Restaurant Apps?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-start space-x-4"
            >
              <div className="bg-primary text-white p-3 rounded-full">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Increased Revenue</h3>
                <p className="text-gray-600">Drive more orders and improve customer engagement through digital channels.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-start space-x-4"
            >
              <div className="bg-primary text-white p-3 rounded-full">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Streamlined Operations</h3>
                <p className="text-gray-600">Automate order management and reduce operational overhead.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Digitize Your Restaurant?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's create a custom mobile app that takes your restaurant to the next level.
          </p>
          <Link
            to="/contact"
            className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition duration-300"
          >
            Get Started Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RestaurantApps; 